// assets/preorder-form.js — HoB Summer Route preorder form (Unit 4 §2.6).
// type="module", framework-free.
//
// Folds in: bare-numeric variantId (contract-data C1), priceFormatted from Liquid
// `money` (contract-data M2), UTC date formatting (contract-data M1), unavailable
// rows excluded from payload (contract-data I3), idempotencyKey reused on retry (§3.4).
//
// ICON_MINUS / ICON_PLUS are the real theme inline SVGs (icon-minus.svg / icon-plus.svg,
// the same icons bulk-order.liquid:176,197 injects). The block stashes them in
// <template data-icon-minus> / <template data-icon-plus> via inline_asset_content so this
// framework-free module can clone them into the .quantity-selector steppers with no extra
// HTTP request.

class PreorderForm {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-preorder-formel]');
    this.apiBase = root.dataset.apiBase.replace(/\/$/, '');
    this.proxyPath = root.dataset.proxyPath;
    this.variants = JSON.parse(root.dataset.variants || '[]');
    this.days = [];
    this.idempotencyKey = crypto.randomUUID();   // §3.4 — once per form mount, reused on retry
    this.lockedStop = null;   // set by preorder:setstop broadcast from store-locator

    // Real theme inline SVGs injected by the block (inline_asset_content).
    this.iconMinus = root.querySelector('[data-icon-minus]')?.innerHTML ?? '';
    this.iconPlus = root.querySelector('[data-icon-plus]')?.innerHTML ?? '';

    document.addEventListener('preorder:setstop', (e) => this.setLockedStop(e.detail && e.detail.name));

    this.bind();
    this.renderLines();
    this.loadSchedule();
  }

  setLockedStop(name) {
    this.lockedStop = (name || '').trim() || null;
    this.applyLockedStop();
  }

  applyLockedStop() {
    if (!this.lockedStop || !this.days.length) return;
    const nameLower = this.lockedStop.toLowerCase();
    // Find the first day that has a stop matching lockedStop name (case-insensitive).
    let matchDay = null;
    let matchSv = null;
    for (const d of this.days) {
      for (const sv of (d.stopVisits || [])) {
        if ((sv.popupStop.name || '').trim().toLowerCase() === nameLower) {
          matchDay = d;
          matchSv = sv;
          break;
        }
      }
      if (matchDay) break;
    }

    const e = this.els();
    const lockedLabel = this.root.querySelector('[data-locked-label]');

    if (matchDay && matchSv) {
      // Set day select, rebuild stops, set + lock stop select.
      e.day.value = matchDay.id;
      this.populateStops();   // rebuilds stop options for this day (also re-applies lock via lockedStop)
      // Show locked-stop echo.
      if (lockedLabel) {
        lockedLabel.textContent = `Pickup at ${this.lockedStop}`;
        lockedLabel.hidden = false;
      }
    } else {
      // Stop not yet in schedule — fall back gracefully: leave selects normal, still echo colony name.
      if (lockedLabel && this.lockedStop) {
        lockedLabel.textContent = `Pickup at ${this.lockedStop} (confirm stop below)`;
        lockedLabel.hidden = false;
      }
    }
  }

  els() {
    return {
      day:    this.form.querySelector('[data-field-day]'),
      stop:   this.form.querySelector('[data-field-stop]'),
      name:   this.form.querySelector('[data-field-name]'),
      phone:  this.form.querySelector('[data-field-phone]'),
      email:  this.form.querySelector('[data-field-email]'),
      notes:  this.form.querySelector('[data-field-notes]'),
      submit: this.form.querySelector('[data-submit]'),
      lines:  this.form.querySelector('[data-lines]'),
      error:  this.root.querySelector('[data-error]'),
      errorMsg: this.root.querySelector('[data-error-msg]'),
      success:  this.root.querySelector('[data-success]'),
    };
  }

  bind() {
    const e = this.els();
    e.day.addEventListener('change', () => this.populateStops());
    this.form.addEventListener('submit', (ev) => { ev.preventDefault(); this.submit(); });
    e.lines.addEventListener('click', (ev) => this.onStepper(ev));
  }

  escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // --- Variant pills (from data-variants; NO Storefront API HTTP call) ---
  // Mirrors bulk-order.liquid pill markup, renamed to .preorder-form__pill* (§FIX1).
  // Hooks preserved: data-line, data-variant-id (bare numeric), data-step, data-qty.
  renderLines() {
    const e = this.els();
    // `+=` so the visible <legend> already in [data-lines] is preserved.
    e.lines.innerHTML += this.variants.map((v) => {
      const unavailable = !v.available;
      const mod = unavailable ? ' preorder-form__pill--unavailable' : '';
      const name = this.escapeHtml(v.variantTitle);
      const id = this.escapeHtml(v.variantId);
      const imgHtml = v.image
        ? `<img src="${this.escapeHtml(v.image)}" alt="${name}" width="40" height="40" loading="lazy">`
        : '';
      return `
        <div class="preorder-form__pill${mod}" data-line data-variant-id="${id}">
          <div class="preorder-form__pill-image">${imgHtml}</div>
          <span class="preorder-form__pill-name">${name}</span>
          <div class="preorder-form__pill-qty">
            ${unavailable
              ? `<span class="preorder-form__pill-sold-out">Sold out</span>`
              : `<button type="button" class="preorder-form__pill-qty-btn" data-step="-1"
                   aria-label="Decrease ${name}" disabled>
                   <span class="svg-wrapper">${this.iconMinus}</span>
                 </button>
                 <input type="number" value="0" min="0" max="100" inputmode="numeric"
                        class="preorder-form__pill-qty-input" data-qty
                        aria-label="${name} quantity">
                 <button type="button" class="preorder-form__pill-qty-btn" data-step="1"
                   aria-label="Increase ${name}">
                   <span class="svg-wrapper">${this.iconPlus}</span>
                 </button>`
            }
          </div>
        </div>`;
    }).join('');
  }

  onStepper(ev) {
    const btn = ev.target.closest('[data-step]');
    if (!btn || btn.disabled) return;
    const qty = btn.closest('.preorder-form__pill-qty');
    if (!qty) return;
    const input = qty.querySelector('[data-qty]');
    const next = Math.max(0, Math.min(100, (parseInt(input.value, 10) || 0) + Number(btn.dataset.step)));
    input.value = next;
    // Mirror bulk-order.liquid:174 — disable minus when qty reaches 0.
    const minusBtn = qty.querySelector('[data-step="-1"]');
    if (minusBtn) minusBtn.disabled = next === 0;
  }

  // --- Days + embedded stops (cross-origin GET; CORS-locked to hausofbabka.com) ---
  async loadSchedule() {
    try {
      const res = await fetch(`${this.apiBase}/api/route/days`);
      this.days = await res.json();
      const sel = this.els().day;
      this.days.forEach((d) => {
        const o = document.createElement('option');
        o.value = d.id;
        // contract-data M1: format from UTC parts; payload `date` is ...T00:00:00.000Z.
        o.textContent = new Date(d.date).toLocaleDateString(undefined,
          { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
        sel.appendChild(o);
      });
      // Apply any stop broadcast that arrived before days finished loading.
      this.applyLockedStop();
    } catch { this.showError('Could not load pickup days. Please refresh.'); }
  }

  // Day -> stops cascade uses the EMBEDDED stopVisits[].popupStop (Unit 3 C3); no /api/route/stops call.
  populateStops() {
    const e = this.els();
    const day = this.days.find((d) => d.id === e.day.value);
    e.stop.innerHTML = '<option value="" disabled selected>Pickup stop</option>';
    if (!day) { e.stop.disabled = true; return; }
    (day.stopVisits || [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .forEach((sv) => {
        const o = document.createElement('option');
        o.value = sv.popupStop.id;
        o.textContent = sv.popupStop.name + (sv.popupStop.address ? ` — ${sv.popupStop.address}` : '');
        e.stop.appendChild(o);
      });
    e.stop.disabled = false;

    // If a stop is locked (broadcast from store-locator), pre-select + disable it on this day.
    if (this.lockedStop) {
      const nameLower = this.lockedStop.toLowerCase();
      const opts = [...e.stop.options];
      const match = opts.find((o) => o.textContent.split(' — ')[0].trim().toLowerCase() === nameLower);
      if (match) {
        e.stop.value = match.value;
        e.stop.disabled = true;
      } else {
        // Chosen day lacks this stop — fall back to normal enabled behavior.
        e.stop.disabled = false;
      }
    }
  }

  // --- Payload + POST to the App Proxy (same-origin, no secret) ---
  collectLines() {
    return [...this.form.querySelectorAll('[data-line]')]
      // contract-data I3: skip unavailable rows so one OOS item can't poison the order.
      .filter((row) => !row.classList.contains('preorder-form__pill--unavailable'))
      .map((row) => ({ variantId: row.dataset.variantId,            // BARE NUMERIC (contract-data C1)
                       qty: parseInt(row.querySelector('[data-qty]').value, 10) || 0 }))
      .filter((l) => l.qty > 0);
  }

  toE164(raw) {
    const d = (raw || '').replace(/[^\d+]/g, '');
    if (d.startsWith('+')) return d;
    const digits = d.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return d;
  }

  validate(payload) {
    if (!payload.routeDayId) return 'Choose a pickup day.';
    if (!payload.popupStopId) return 'Choose a pickup stop.';
    if (!payload.name) return 'Add your name.';
    if (!payload.phone && !payload.email) return 'Add a phone or email.';
    if (!payload.lines.length) return 'Add at least one flavor.';
    return null;
  }

  async submit() {
    const e = this.els();
    this.clearError();
    const phoneRaw = e.phone.value.trim();
    const payload = {
      idempotencyKey: this.idempotencyKey,   // reused on retry (§3.4)
      name: e.name.value.trim(),
      phone: phoneRaw ? this.toE164(phoneRaw) : undefined,
      email: e.email.value.trim() || undefined,
      popupStopId: e.stop.value,
      routeDayId: e.day.value,
      lines: this.collectLines(),
      notes: e.notes.value.trim() || undefined,
    };
    const err = this.validate(payload);
    if (err) { this.showError(err); return; }

    e.submit.setAttribute('aria-disabled', 'true');   // base.css:1356–1361 → opacity .5 / not-allowed
    const restore = e.submit.textContent;
    e.submit.textContent = 'Placing…';
    try {
      const res = await fetch(this.proxyPath, {          // SAME-ORIGIN; Shopify signs + forwards
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 201 || res.status === 200) { this.onSuccess(); return; }   // both = success
      let msg = 'Something went wrong. Please try again.';
      if (res.status === 429) msg = "You've reached the preorder limit for this day.";
      else if (res.status === 404) msg = 'That day or stop is no longer available.';
      else if (res.status === 422) msg = 'One or more flavors are unavailable.';
      try { const b = await res.json(); if (b && b.error) msg = b.error; } catch {}
      this.showError(msg);   // idempotencyKey preserved → retry is safe
    } catch {
      this.showError('Network error. Check your connection and try again.');
    } finally {
      e.submit.removeAttribute('aria-disabled');
      e.submit.textContent = restore;
    }
  }

  onSuccess() {
    const e = this.els();
    this.form.hidden = true;
    e.success.hidden = false;
    e.success.focus();
  }

  showError(message) {
    const e = this.els();
    e.errorMsg.textContent = message;
    e.error.hidden = false;
    e.error.focus();
  }
  clearError() { this.els().error.hidden = true; }
}

document.querySelectorAll('[data-preorder-form]').forEach((el) => new PreorderForm(el));
